<template>
  <div
    :data-testid="dataTestid"
    :class="
      $attrs.class ||
      twMerge(
        'flex flex-col max-sm:items-center max-lg:bg-primary',
        appendClass
      )
    "
  >
    <TsTypography
      v-if="props.heading"
      :append-class="
        twMerge(
          'text-idle-white lg:font-semibold max-sm:text-center mb-3 sm:mb-2 max-sm:!text-[24px]',
          props.headingClass
        )
      "
    >
      {{ props.heading || useTranslation("footerNewsletterHeading") }}
    </TsTypography>

    <TsTypography
      v-if="props.description"
      variant="sm"
      :append-class="
        twMerge(
          'text-idle-white lg:font-normal max-sm:text-center mb-6 leading-relaxed',
          props.descriptionClass
        )
      "
      :text="props.description || useTranslation('footerNewsletterDesc')"
    >
    </TsTypography>

    <form
      :data-testid="dataTestid + '-form'"
      @submit.prevent="handleNewsletterSubscribe()"
      novalidate
      class="w-full sm:max-w-lg sm:flex"
    >
      <TsInput
        @input="handleInputStart('email')"
        v-model="email"
        icon="lucide:mail"
        icon-position="left"
        input-class="text-idle-white placeholder:text-idle-white"
        icon-class="text-idle-white"
        :append-class="[
          'lg:hidden sm:min-w-md',
          is_news_letter_input_focused && 'border-idle-white',
        ]"
        @focus="handleFocus"
        @blur="handleBlur"
        :placeholder="useTranslation('email', 'E-mail')"
        :data-testid="dataTestid + '-email-input'"
        required
        autocomplete="newsletter-email"
      />

      <div v-if="!isDesktopScreen" class="lg:hidden max-w-full mx-auto">
        <TsButton
          :data-testid="dataTestid + '-submit-button'"
          type="submit"
          :is-loading="newsletterSubscribeState.status === 'loading'"
          :label="useTranslation('footerNewsletterButtonCTA', 'Abonneren')"
          append-class="bg-idle-white hover:bg-natural-soft-white text-primary border-0 font-medium max-sm:mt-5 sm:ml-4"
        />
      </div>

      <div class="flex flex-col w-full gap-1">
        <TsInputGroup append-class="max-lg:hidden w-full">
          <TsInput
            v-model="email"
            @input="handleInputStart('email')"
            icon="lucide:mail"
            icon-position="left"
            icon-class="w-5 text-idle-white"
            input-class="text-idle-white placeholder:text-idle-white"
            :append-class="[
              'sm:min-w-md text-idle-white',
              is_news_letter_input_focused && 'border-idle-white',
            ]"
            @focus="handleFocus"
            @blur="handleBlur"
            :placeholder="useTranslation('email', 'E-mail')"
            :data-testid="dataTestid + '-email-input'"
            required
            autocomplete="newsletter-email"
          />
          <TsButton
            :data-testid="dataTestid + '-submit-button'"
            type="submit"
            :is-loading="newsletterSubscribeState.status === 'loading'"
            :label="useTranslation('footerNewsletterButtonCTA', 'Abonneren')"
            append-class="bg-idle-white hover:bg-natural-soft-white text-primary border-0 font-medium"
          />
        </TsInputGroup>
        <p
          v-if="newsletterSubscribeState.message"
          :class="
            twJoin(
              'text-sm font-semibold',
              newsletterSubscribeState.status === 'success'
                ? 'text-green-400'
                : newsletterSubscribeState.status === 'failed'
                ? 'text-red-300/70'
                : ''
            )
          "
        >
          {{ newsletterSubscribeState.message }}
        </p>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { twJoin, twMerge, type ClassNameValue } from "tailwind-merge";
import { ref, onBeforeUnmount } from "vue";
import { EcomService } from "~/services/ecom.service.js";
import type { APIState } from "~/types/api-state.type.js";
import { useStateModifier } from "~/composables/useStateModifier.js";
import { useTranslation } from "~/composables/useTranslation";
import { useScreenSize } from "~/composables/useScreenSize";

const { isDesktopScreen } = useScreenSize();
const { $customClientErrorHandler } = useNuxtApp();

const TsInputGroup = defineAsyncComponent(
  () => import("~/components/molecules/TsInputGroup.vue")
);

const email = ref("");

const authStore = useAuthStore();
// const subscribeButton=useTranslation("footerNewsletterButtonCTA","Abonneren")
if (authStore.user) email.value = (authStore.user as any).email;

type Props = {
  subscribeIcon?: string;
  heading?: string;
  description?: string;
  descriptionClass?: ClassNameValue;
  headingClass?: ClassNameValue;
  appendClass?: ClassNameValue;
  dataTestid?: string;
  hasIcon?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  titleIcon: "lucide:tag",
  subscribeIcon: "bi:arrow-right",
  titleIconSize: "36",
  heading: "Always informed of our actions",
  description: "Keep me updated about products, news, tips and offers.",
  dataTestid: "organisms-newsletter",
  hasIcon: true,
});

const is_news_letter_input_focused = ref(false);

const handleFocus = (e: Event, isFocused: boolean) => {
  if (isFocused) is_news_letter_input_focused.value = true;
};

const handleBlur = (e: Event) => {
  is_news_letter_input_focused.value = false;
};

const newsletterSubscribeState = ref<APIState>({
  status: "idle",
});

async function handleNewsletterSubscribe() {
  if (!email.value) {
    useStateModifier(
      newsletterSubscribeState.value,
      "failed",
      useTranslation("emailFeildRequired", "Het e-mailadresveld is verplicht.")
    );
    return;
  }

  logNewsLetterSubscribe(email.value); // GTM logger
  useStateModifier(newsletterSubscribeState.value, "loading", "");

  try {
    const res = await EcomService.subscribeToNewsletter({
      email_address: email.value,
    });

    // Success path
    useStateModifier(
      newsletterSubscribeState.value,
      "success",
      useTranslation(
        "thankYouForSubscribing",
        "Bedankt voor uw inschrijving op onze nieuwsbrief."
      )
    );
  } catch (e: any) {

    const errorMeta = {
      email,
      newsletterSubscribeState,
      footerError: true,
    };

    // Specific email error
    if (
      e.data?.error?.data?.errors?.email_address &&
      Array.isArray(e.data.error.data.errors.email_address)
    ) {
      const msg = e.data.error.data.errors.email_address[0];

      useStateModifier(newsletterSubscribeState.value, "failed", msg);
      $customClientErrorHandler(
        e,
        "low",
        "Footer Newsletter Error - email_address specific error",
        errorMeta
      );
    } else {
      // Generic error
      const msg = useTranslation("somethingWentWrong", "Er is iets misgegaan.");

      useStateModifier(newsletterSubscribeState.value, "failed", msg);
      $customClientErrorHandler(
        e,
        "low",
        "Footer Newsletter Error - generic failure",
        errorMeta
      );
    }
  }
}


// GTM logger for form start

let formInteracted = false;

function handleInputStart(formField: string) {
  if (!formInteracted) {
    formInteracted = true;
    logEvent({
      options: {
        eventAction: "form_start",
        eventProperties: {
          form_field: formField,
        },
        eventInteraction: true,
        eventLabel: "Newsletter Form Started",
        eventName: "trackFormStart",
      },
    });
  }
}

function logNewsLetterSubscribe(emailAddress: string) {
  logEvent({
    options: {
      eventAction: "form_submit",
      eventProperties: {
        email: emailAddress,
      },
      eventInteraction: true,
      eventLabel: "Newsletter Form Submitted",
      eventName: "trackNewsletterSubscribe",
    },
  });
}

// Clean up any references when component is unmounted
onBeforeUnmount(() => {
  email.value = "";
  formInteracted = false;
  newsletterSubscribeState.value = {
    status: "idle",
    message: "",
  };
});
</script>
